import {Expressions, EvalEnv, Component, Plugins} from '@packages/systems/wfdl';
import {getDynamoBindingPaths} from '@packages/systems/dynamo/wfdl';
import {ExpressionElement, type Expression} from '@packages/systems/wfdl-ast';
import {ExpansionProps} from '../types';
import {isInsideAnyDynamicList} from '@packages/utilities/platform';
import {isTextHolder} from '@packages/utilities/elementTypeUtils';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';

function isDynamoBinding(binding: Expression): boolean {
  if (Expressions.isBinding(binding)) {
    const dynamoBindings = getDynamoBindingPaths(binding);
    return dynamoBindings.length === 1;
  }
  return false;
}

function isEmptyDynamoVideoBinding(
  binding: Expression,
  env: EvalEnv.EvalEnvType
): boolean {
  const videoData = Expressions.evaluate(env, binding);
  return !videoData?.embed?.meta?.html;
}

function isEmptyListBinding(
  binding: Expression,
  env: EvalEnv.EvalEnvType
): boolean {
  const data = Expressions.evaluate(env, binding);
  return !Array.isArray(data) || data.length === 0;
}

function isEmptyImageBinding(
  binding: Expression,
  env: EvalEnv.EvalEnvType
): boolean {
  const imageData = Expressions.evaluate(env, binding);
  return !imageData || imageData.id === '';
}

const isItemRefField = (expr: Expression) => {
  const callExpr = Expressions.getCall(expr);
  const select = callExpr ? Expressions.getSelect(callExpr.arg) : null;
  const itemFieldSlug = select?.prop;
  return Boolean(itemFieldSlug && itemFieldSlug.includes('.'));
};

function needsEmptyClass(
  env: EvalEnv.EvalEnvType,
  components: Component.ComponentMap,
  element: ExpressionElement,
  focusedComponentType: Plugins.QualifiedElement,
  itemId: string,
  runtimeEnvironment: Environment
): boolean {
  const ubFields = Expressions.getRecord(Expressions.getElementData(element));
  if (!ubFields) return false;

  if (
    ubFields.video &&
    isDynamoBinding(ubFields.video) &&
    isEmptyDynamoVideoBinding(ubFields.video, env)
  ) {
    return true;
  }

  if (ubFields.json) {
    const ubJsonFields = Expressions.getRecord(ubFields.json);
    if (
      ubJsonFields &&
      ubJsonFields.items &&
      isDynamoBinding(ubJsonFields.items) &&
      isEmptyListBinding(ubJsonFields.items, env)
    ) {
      return true;
    }
  }

  if (
    ubFields.children &&
    isDynamoBinding(ubFields.children) &&
    isEmptyListBinding(ubFields.children, env)
  ) {
    // This allows for on-canvas editing of text elements that are bound. The 'w-dyn-bind-empty'
    // class should be excluded if all of the following conditions are true:
    //  1. The runtime environment is Designer
    //  2. The collection item is loaded
    //  3. The element is a Text element
    //  4. The element uses Universal Bindings
    //  5. The element is not contained within a Collection List
    //  6. The element is not bound to a single reference field
    if (
      runtimeEnvironment === WebflowEnvironments.DESIGNER &&
      focusedComponentType &&
      itemId &&
      isTextHolder(element) &&
      !isItemRefField(ubFields.children)
    ) {
      const currentComponent = components.get(focusedComponentType);
      if (currentComponent) {
        const rootExpr = Component.getRender(currentComponent);
        if (!isInsideAnyDynamicList(rootExpr)(element)) {
          return false;
        }
      }
    }

    return true;
  }

  if (
    ubFields.img &&
    isDynamoBinding(ubFields.img) &&
    isEmptyImageBinding(ubFields.img, env)
  ) {
    return true;
  }

  return false;
}

export function addDynBindEmptyClass<T extends ExpansionProps>(
  props: T,
  runtimeEnvironment: Environment
): T {
  const {
    classes,
    env,
    components,
    element,
    environmentContext,
    pluginsContext,
  } = props;

  if (
    needsEmptyClass(
      env,
      components,
      element,
      environmentContext.get('focusedComponentType'),
      pluginsContext.getIn(['Dynamo', 'itemId']),
      runtimeEnvironment
    )
  ) {
    const updatedClasses = classes.push('w-dyn-bind-empty');
    return {...props, classes: updatedClasses};
  }
  return props;
}
